'use client'
import { useMemo } from 'react'
import { type PromotionNavigation } from '@shipt/cdui-modules/host'
import { getRetailerFeaturedPromotionsUrl } from '@shared/constants/routes'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicFreeItemPromoModal } from '@/features/shop/components/Modal/FreeItemPromoModal/DynamicFreeItemPromoModal'

type ProductPromotion = Parameters<NonNullable<PromotionNavigation['openPromotion']>>[1]

/**
 * Makes a CDUI product card's promotion line tappable, with the same rule and
 * destinations as Segway's own ProductCardClickablePromotion where a free item callout
 * opens the free item modal, a brand promotion opens its featured promotions page.
 * Anything else (BOGO, a bare callout) stays plain text.
 */
export const canOpenPromotion = (_productId: string, promotion: ProductPromotion): boolean =>
  promotion.calloutCategoryId !== undefined || promotion.categoryId !== undefined

export const usePromotionNavigation = (): PromotionNavigation => {
  const router = useSegwayRouter()
  const storeName = useUserStoreName()
  const { openDialog } = useHandleDialog()

  return useMemo(
    () => ({
      canOpenPromotion,
      openPromotion: (_productId, { calloutCategoryId, categoryId, promotionId }) => {
        if (calloutCategoryId !== undefined) {
          openDialog(DynamicFreeItemPromoModal, {
            labelCategoryId: calloutCategoryId,
            promotionId: promotionId?.toString(),
            // CDUI doesn't say whether this product is already the chosen reward
            selectedFreeItem: undefined,
          })
          return
        }
        if (categoryId === undefined) return

        router.push(
          `${getRetailerFeaturedPromotionsUrl(storeName, categoryId)}?promotion_category_id=${categoryId}`,
        )
      },
    }),
    [openDialog, router, storeName],
  )
}
